import React, { useEffect, useRef, useState } from "react";
import { TbReceipt } from "react-icons/tb";
import { Button, CloseButton, DatePicker, Divider, CustomFields, Input, Portal, Select, Toast, Form, AnimatedTick, FileInput, ReceiptStore } from "../../../../../../Components";
import ExpenseAttachments from "./attachments";
import { apis, get, post } from "../../../../../../Utils/axios";
import useFetch from "../../../../../../Hooks/useFetch";
import moment from "moment";
import { useClientSettings } from "../../../../../../Hooks/useClientSettings";
import { FiFileText, FiPlus, FiTrash2 } from "react-icons/fi";

function AddReceipt({ onReload = () => {} }) {
	const { isEnabled } = useClientSettings();
	const isPdfFile = (file = {}) => {
		const name = `${file?.name || ""}`.toLowerCase();
		const url = `${file?.url || ""}`.toLowerCase();
		const mime = `${file?.mimeType || file?.mimetype || file?.type || ""}`.toLowerCase();
		return mime.includes("pdf") || name.endsWith(".pdf") || url.includes(".pdf");
	};
	const getFileName = (file = {}, index = 0) => file?.name || file?.originalName || `Receipt ${index + 1}`;
	const getFileMeta = (file = {}) => (isPdfFile(file) ? "PDF document" : "Image receipt");
	const removeAttachment = (targetFile) => {
		setAttachments((prev) =>
			prev.filter((attachment) => {
				const file = attachment?.file || {};
				if (targetFile?.key && file?.key) return file.key !== targetFile.key;
				if (targetFile?.id && file?.id) return file.id !== targetFile.id;
				if (targetFile?.url && file?.url) return file.url !== targetFile.url;
				return file !== targetFile;
			})
		);
	};

	const [Show, setShow] = useState(false);
	const [OCRData, setOCRData] = useState();
	const [RunningOcr, setRunningOcr] = useState(false);
	const [Attachments, setAttachments] = useState([]);
	const [Loading, setLoading] = useState(false);

	const initial = {
		merchant: "",
		amount: "",
		date: "",
		description: "",
		category: {},
	};

	const { response, loading } = useFetch(apis.getExpenseCategories, null, { categories: [] });
	const [Success, setSuccess] = useState(false);

	const [Values, setValues] = useState(initial);

	const GET_CUSTOM_FIELDS = useRef();

	useEffect(() => {
		if (Attachments.length === 1) {
			//Run OCR only for first receipt
			runOCR(Attachments[0]);
		}
	}, [Attachments]);

	useEffect(() => {
		const params = new URLSearchParams(window.location.search);
		const openFlag = params.get("openAddReceipt");
		const openCount = Number(openFlag);
		const shouldOpen = openFlag === "true" || (Number.isFinite(openCount) && openCount >= 1);
		if (shouldOpen) {
			setShow(true);
			Toast.success("Upload received. Use Receipt Store below to select your uploaded file.");
			params.delete("openAddReceipt");
			const query = params.toString();
			window.history.replaceState(null, "", `${window.location.pathname}${query ? `?${query}` : ""}`);
		}
	}, []);

	const updateValues = (obj) => {
		setValues((prev) => ({ ...prev, ...obj }));
	};

	const runOCR = (obj) => {
		setRunningOcr(true);
		get(apis.runOcr, { fileId: obj.file.id }, (r, e) => {
			if (r) {
				setOCRData(r);
				setValues((prev) => ({ ...prev, merchant: r.vendorName, date: r.receiptDate, amount: r.total }));
				setRunningOcr(false);
			} else if (e) {
				setRunningOcr(false);
			}
		});
	};

	const onSubmit = () => {
		if (!Values.merchant) {
			return Toast.error("Please enter expense merchant");
		}
		if (!Values.amount) {
			return Toast.error("Please enter expense amount");
		}
		if (!Values.date) {
			return Toast.error("Please select expense date");
		}
		if (!Values.description) {
			return Toast.error("Please enter expense description");
		}
		if (!Attachments.length) {
			return Toast.error("Please add receipt photo");
		}

		let customFields;
		if (isEnabled("ORGANIZATION")) {
			if (!Values.category?.value) {
				return Toast.error("Please select expense category");
			}

			customFields = GET_CUSTOM_FIELDS.current();
			if (!customFields) {
				return;
			}
		}

		setLoading(true);
		post(
			apis.addReceipt,
			{
				...Values,
				categoryId: Values.category.value,
				customFields,
				attachments: Attachments.map((a) => a.file.key),
				date: moment(Values.date).format("DD-MM-YYYY"),
			},
			(r, e) => {
				setLoading(false);
				if (r) {
					setValues(initial);
					setAttachments([]);
					setSuccess(true);
					onReload();
				} else if (e) {
					Toast.handleError(e);
				}
			}
		);
	};

	if (Success) {
		return (
			<>
				<Portal>
					<div className="absolute bg-[rgba(0,0,0,0.5)] top-0 left-0 right-0 bottom-0 z-20 flex-center fade-in">
						<div className="p-8 bg-canvas rounded-xl">
							<div className="h-20 flex-center">
								<AnimatedTick />
							</div>
							<div className="mt-4 text-center">
								<h1>Receipt created</h1>
								<div className="mt-4 text-label">
									Expense have been added successfully, <br />
									you can track all your expenses in Expenses section
								</div>
								<div className="gap-2 mt-6 flex-center">
									<Button
										onClick={() => {
											setSuccess(false);
											setShow(false);
										}}
									>
										Close
									</Button>
									<Button variant="primary" onClick={() => setSuccess(false)}>
										Add More
									</Button>
								</div>
							</div>
						</div>
					</div>
				</Portal>
			</>
		);
	}

	return (
		<>
			<Button onClick={() => setShow(true)} variant="primary">
				<FiPlus className="mr-2" />
				Add Receipt
			</Button>
			{Show && (
				<Portal>
					<div className="absolute inset-0 z-20 bg-[rgba(15,23,42,0.28)] fade-in">
						<div className="flex h-full w-full overflow-hidden backdrop-blur-[2px]">
							<div className="mx-auto flex h-full w-full max-w-[90rem] flex-col overflow-hidden bg-light lg:flex-row">
								<div className="min-h-0 flex-1 overflow-y-auto border-b border-light lg:border-b-0 lg:border-r">
									<ExpenseAttachments runningOCR={RunningOcr} attachments={Attachments} updateAttachments={setAttachments} />
								</div>
								<Form onSubmit={onSubmit} className="flex h-full w-full flex-col bg-canvas lg:w-[26rem] lg:min-w-[26rem] xl:w-[30rem] xl:min-w-[30rem]">
									<div className="flex-1 overflow-x-hidden overflow-y-auto">
										<div className="sticky top-0 z-10 border-b bg-canvas/95 px-4 py-4 backdrop-blur flex-center-between sm:px-5">
											<div>
												<h3 className="align-center gap-x-2">
													<TbReceipt />
													Add Receipt
												</h3>
												<div className="mt-1 text-xs text-secondary">
													Upload a file, confirm the details, then create the expense.
												</div>
											</div>
											<CloseButton onClick={() => setShow(false)} size="2xl" />
										</div>
										<div className="px-4 py-4 sm:px-5">
											<div className="rounded-xl border border-surface bg-light px-3 py-2.5 text-xs text-secondary">
												Step 1: Add a receipt image or PDF. Step 2: Review the extracted details. Step 3: Complete any missing fields and submit.
											</div>
											<div className="mt-4 rounded-xl border border-surface bg-light p-3.5">
												<div className="flex flex-col gap-3">
													<div>
														<h5>Receipt File</h5>
														<div className="mt-1 text-xs text-secondary">
															{Attachments.length ? `${Attachments.length} file${Attachments.length > 1 ? "s" : ""} added. You can replace or add more files below.` : "Start here by uploading a receipt image or PDF, or choose one from Receipt Store."}
														</div>
													</div>
													<div className="flex flex-col gap-2 sm:flex-row">
														<FileInput url={apis.uploadReceipt} data={{}} extensions={[".jpg", ".jpeg", ".png", ".pdf"]} onUpload={(obj) => setAttachments((prev) => [...prev, obj])}>
															<div className="btn btn-dark btn-sm w-full cursor-pointer gap-2 sm:w-auto">
																<FiPlus />
																{Attachments.length ? "Upload Another" : "Upload Receipt"}
															</div>
														</FileInput>
														<ReceiptStore className="w-full sm:w-auto" onSelect={(file) => setAttachments((prev) => [...prev, { file }])} />
													</div>
													{Attachments.length > 0 && (
														<div className="grid gap-2 sm:grid-cols-2">
																{Attachments.map((attachment, index) => {
																	const file = attachment?.file || {};
																	const isPdf = isPdfFile(file);
																	return (
																		<div key={file?.url || file?.key || index} className="flex items-center gap-3 rounded-xl border border-surface bg-canvas p-2.5 shadow-sm">
																			<div className="flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden rounded-lg bg-light">
																				{isPdf ? (
																					<FiFileText className="text-lg text-secondary" />
																				) : (
																					<img src={file?.url} alt={getFileName(file, index)} className="h-full w-full object-cover" />
																				)}
																			</div>
																			<div className="min-w-0 flex-1">
																				<div className="truncate text-sm font-semibold text-dark">{getFileName(file, index)}</div>
																				<div className="mt-0.5 text-xs text-secondary">{getFileMeta(file)}</div>
																			</div>
																			<button type="button" onClick={() => removeAttachment(file)} className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-light text-dangerText transition-colors hover:bg-red-50" aria-label={`Remove ${getFileName(file, index)}`}>
																				<FiTrash2 className="text-base" />
																			</button>
																		</div>
																	);
																})}
														</div>
													)}
												</div>
											</div>
											<div className="mt-5">
												<h4>Expense Info</h4>
												<div className="mt-1 text-xs text-secondary">
													Keep these details aligned with the receipt so reimbursement is faster.
												</div>
											</div>
											<Input value={Values.merchant} onChange={(merchant) => updateValues({ merchant })} required className="mt-4" label="Merchant" placeholder="Merchant Name" />
											<Input min={0} step="any" value={Values.amount} onChange={(amount) => updateValues({ amount: amount ? Number(amount) : "" })} required type="number" label="Transaction Amount" placeholder="Enter Amount" className="mt-4" />
											<DatePicker maxDate={moment().valueOf()} backDateSelection required date={Values.date} setDate={(date) => updateValues({ date })} label="Transaction Date" className="mt-4" btnClass="w-full justify-between" />
											{isEnabled("ORGANIZATION") && (
												<Select
													loading={loading}
													options={response.categories.map((c) => ({ value: c.id, label: c.name }))}
													selected={Values.category}
													onChange={(category) => updateValues({ category })}
													required
													className="mt-4"
													btnClass="w-full"
													placeholder="Select Category"
													label="Category"
												/>
											)}
											<Input value={Values.description} onChange={(description) => updateValues({ description })} required className="mt-4" label="Description" placeholder="Explain the purpose of expense" />
										</div>
										{Values.category?.value && (
											<div className="border-t px-4 py-4 sm:px-5">
												<h4>Additional Details</h4>
												<CustomFields className="mt-4" categoryId={Values.category?.value} getCustomFields={GET_CUSTOM_FIELDS} activity="EXPENSE" />
											</div>
										)}
									</div>
									<div className="border-t bg-canvas px-4 py-4 sm:px-5">
										<Button type="submit" variant="primary" className="w-full" loading={Loading}>
											Create Expense
										</Button>
									</div>
								</Form>
							</div>
						</div>
					</div>
				</Portal>
			)}
		</>
	);
}

export default AddReceipt;
